import { Routes, Route } from "react-router";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "@/components/error-boundary";
import ArcLayout from "@/components/layout/arc-layout";

// Account pages (auth) - these are outside ArcLayout
const Login = lazy(() => import("@/pages/account/login"));
const Signup = lazy(() => import("@/pages/account/signup"));
const Forgot = lazy(() => import("@/pages/account/forgot"));
const ForgotToken = lazy(() => import("@/pages/account/forgot/[token]"));
const ActivateToken = lazy(() => import("@/pages/account/activate/[token]"));

// Error pages - outside ArcLayout
const NotFound = lazy(() => import("@/pages/404"));
const ServerError = lazy(() => import("@/pages/500"));

// Print page - outside ArcLayout, for PDF export via Playwright
const PrintPage = lazy(() => import("@/pages/print/index"));

// Loading fallback component
const PageLoader = () => {
  return (
    <div className="flex items-center justify-center h-screen bg-surface">
      <div className="animate-arc-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
    </div>
  );
};

/**
 * Layout wrapper component that renders ArcLayout.
 * Content is rendered through the panel system, not through Outlet.
 * This allows the layout to persist between route changes, preventing
 * unnecessary re-mounts of the sidebar and other layout components.
 */
const LayoutWrapper = () => {
  return (
    <ErrorBoundary>
      <ArcLayout />
    </ErrorBoundary>
  );
};

export const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/*
         * Routes with ArcLayout - layout persists between navigations.
         * Content is rendered through the panel system (PanelContent),
         * not through Route elements. Routes here are only for URL matching
         * so that syncWithRoute can create the appropriate panel.
         */}
        <Route element={<LayoutWrapper />}>
          {/* Main routes - content rendered by PanelContent based on URL */}
          <Route path="/" element={null} />

          {/* Gallery routes - URLs follow /{doc_type} pattern */}
          <Route path="/dashboards" element={null} />
          <Route path="/trackers" element={null} />
          <Route path="/enrichments" element={null} />
          <Route path="/alerts" element={null} />
          <Route path="/investigations" element={null} />

          {/* Other main routes */}
          <Route path="/chat" element={null} />
          <Route path="/search" element={null} />

          {/* Account app routes (protected) */}
          <Route path="/plan" element={null} />
          <Route path="/workspaces" element={null} />
          <Route path="/organization" element={null} />
          <Route path="/members" element={null} />
          <Route path="/tokens" element={null} />
          <Route path="/channels" element={null} />
          <Route path="/settings" element={null} />

          {/* Demo routes */}
          <Route path="/demo/arc-color-wheel" element={null} />

          {/* Widget Gallery */}
          <Route path="/widgets" element={null} />

          {/* Branches */}
          <Route path="/branches" element={null} />

          {/* Reports (PDF export) */}
          <Route path="/reports" element={null} />
          <Route path="/reports/:id" element={null} />
        </Route>

        {/* Routes without ArcLayout (public/auth pages) - rendered directly */}
        <Route path="/account/login" element={<Login />} />
        <Route path="/account/signup" element={<Signup />} />
        <Route path="/account/forgot" element={<Forgot />} />
        <Route path="/account/forgot/:token" element={<ForgotToken />} />
        <Route path="/account/activate/:token" element={<ActivateToken />} />

        {/* Print route - for PDF export via Playwright (no auth, uses service token) */}
        <Route path="/print/:type/:id" element={<PrintPage />} />

        {/* Error routes */}
        <Route path="/500" element={<ServerError />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};
